import type { RecordFormat, RecordField } from '@adhd/shared';

/**
 * An empty thought record to print and fill in by hand.
 *
 * Deliberately the same object as a filled one — same nameplate, same rules,
 * same numbered rail, same five sections in the same order — with ruled space
 * and boxes where the writing goes. A blank that does not resemble the thing it
 * becomes teaches the wrong shape, and that is the whole job of a blank.
 *
 * One worksheet per sheet of paper. Density is the export screen's problem;
 * here the room to write IS the feature.
 *
 * Sections come from the format's own steps, so a rejection-sensitivity blank
 * has no evidence-for box and a task-paralysis one ends on an action — exactly
 * as they do on screen.
 */
interface Props {
  format: RecordFormat;
}

export function BlankRecord({ format }: Props): JSX.Element {
  const has = (field: RecordField): boolean => format.steps.some((s) => s.field === field);
  const evidenceFor = has('evidenceFor');
  const evidenceAgainst = has('evidenceAgainst');
  let n = 0;
  const step = (): string => String(++n).padStart(2, '0');

  return (
    <article className="bs bs-blank">
      <header className="bs-plate">
        <div>
          {/* The masthead is the constant and the worksheet is the edition.
              It used to print the format's NAME here, and one of them is
              "Spiralling about what might happen" — 34 characters at 26pt
              uppercase serif, three lines of masthead before any content. As a
              kicker it costs one 6.6pt line, and it holds for any format added
              later rather than fitting whichever name is longest today. */}
          <p className="bs-kicker">
            <i>Worksheet</i>
            <s>·</s>
            {format.name}
          </p>
          <h2>Thought record</h2>
        </div>
        <div className="bs-plate-meta">
          ADHD &amp; Me
          <span>Cognitive</span>
          <span>behavioural worksheet</span>
        </div>
      </header>
      <div className="bs-rule-heavy" />
      <div className="bs-folio">
        <span>Situation · Evidence · Response</span>
        <em>Complete by hand</em>
        {/* Given a real height rather than a zero-height border. The design
            arrived with a date rule offering under 4mm of clearance — the one
            field on the sheet you could not actually write in. */}
        <span>
          Date <span className="bs-writeline" />
        </span>
      </div>
      <div className="bs-rule-hair" />

      <div className="bs-body">
        {has('situation') && (
          <Row n={step()} label={format.labels.situation} sub={hint(format, 'situation')}>
            <Lines count={2} />
          </Row>
        )}

        {has('thought') && (
          <Row n={step()} label={format.labels.thought} sub={hint(format, 'thought')}>
            <div className="bs-quote">
              <span className="bs-qm" aria-hidden="true">
                “
              </span>
              <div style={{ flex: 1 }}>
                <Lines count={2} />
              </div>
            </div>
          </Row>
        )}

        <Row n={step()} label="The numbers" sub="Before / after" major>
          <div className="bs-stats">
            {/* Drawn rather than filled: see .bs-slab-fill. A background is at
                the mercy of the print dialog's "Background graphics" switch; an
                SVG rect is content and prints regardless.

                The rect carries no fill attribute on purpose — it is set from
                CSS. `var()` inside an SVG presentation attribute resolves in
                Chrome and silently does not in Safari. */}
            <svg className="bs-slab-fill" preserveAspectRatio="none" aria-hidden="true">
              <rect width="100%" height="100%" />
            </svg>
            <div className="bs-stat">
              <div className="bs-cap">Belief in the thought</div>
              <div className="bs-stat-body">
                <BoxRow />
              </div>
            </div>
            <div className="bs-stat">
              {/* Two, matching the filled sheet. One slot was never enough:
                  a check-in can name several feelings, and the one that moved
                  most is often not the first. */}
              <div className="bs-cap">
                Feelings <em>· name it, then rate it</em>
              </div>
              <div className="bs-stat-body">
                <FeelingSlot />
                <FeelingSlot />
              </div>
            </div>
          </div>
        </Row>

        {/* "Both sides" was printed unconditionally, on worksheets that
            deliberately have one — rejection, task paralysis and post-conflict
            all drop the evidence-for step, because listing the evidence you
            were rejected mostly helps you build the case. The label has to tell
            the truth about the sheet it is on, and a lone block takes the whole
            measure rather than sitting in half of it with dead space beside. */}
        {(evidenceFor || evidenceAgainst) && (
          <Row
            n={step()}
            label="The evidence"
            sub={evidenceFor && evidenceAgainst ? 'Both sides' : 'Weigh it up'}
          >
            <div className={evidenceFor && evidenceAgainst ? 'bs-evid' : 'bs-evid bs-evid-single'}>
              {evidenceFor && (
                <div>
                  <span className="bs-chip">{format.labels.evidenceFor}</span>
                  <Lines count={3} small />
                </div>
              )}
              {evidenceAgainst && (
                <div>
                  <span className="bs-chip bs-chip-out">{format.labels.evidenceAgainst}</span>
                  <Lines count={3} small />
                </div>
              )}
            </div>
          </Row>
        )}

        {has('response') && (
          <Row n={step()} label={format.labels.response} sub={hint(format, 'response')} major>
            <div className="bs-resp">
              <div className="bs-resp-bar" aria-hidden="true" />
              <Lines count={3} />
            </div>
          </Row>
        )}
      </div>

      <div className="bs-foot">
        <span>ADHD &amp; Me · Thought record · Private</span>
        <em>Blank · print &amp; write</em>
      </div>
    </article>
  );
}

function Row({
  n,
  label,
  sub,
  major,
  children,
}: {
  n: string;
  label: string;
  sub: string;
  major?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className={major ? 'bs-row bs-row-major' : 'bs-row'}>
      {/* Header line, rule, then content across the whole measure. The 22mm
          rail this replaces cost 28mm with its gutter — 16% of the page spent
          on five two-digit numbers. The numerals are tabular, so the labels
          beside them keep the straight left edge the rail gave for free. */}
      <div className="bs-head">
        <span className="bs-no">{n}</span>
        <span className="bs-lbl">
          {label}
          {sub && <em> · {sub.toLowerCase()}</em>}
        </span>
      </div>
      <div className="bs-sep" />
      {children}
    </section>
  );
}
function Lines({ count, small }: { count: number; small?: boolean }): JSX.Element {
  return (
    <div aria-hidden="true" style={{ marginTop: '1.5mm' }}>
      {Array.from({ length: count }, (_, i) => (
        <span className={small ? 'bs-write bs-write-sm' : 'bs-write'} key={i} />
      ))}
    </div>
  );
}

/** Where the filled sheet prints 85 → 35, the blank gives two boxes to fill. */
function BoxRow(): JSX.Element {
  return (
    <div className="bs-boxrow" aria-hidden="true">
      <span className="bs-box" />
      <span className="bs-arw">→</span>
      <span className="bs-box bs-box-strong" />
    </div>
  );
}

/** A rule for the name of the feeling, then the same before/after pair. */
function FeelingSlot(): JSX.Element {
  return (
    <div className="bs-slot" aria-hidden="true">
      <span className="bs-write bs-write-sm" />
      <div className="bs-boxrow">
        <span className="bs-box" />
        <span className="bs-feel-arw">→</span>
        <span className="bs-box bs-box-strong" />
      </div>
    </div>
  );
}

/**
 * The short line under a rail label. The step's own label, not its hint — the
 * hint is a full sentence and the rail is 22mm wide, so it wrapped to five
 * lines and pushed every section down the page. Blank when it would merely
 * repeat the heading above it.
 */
function hint(format: RecordFormat, field: RecordField): string {
  const step = format.steps.find((s) => s.field === field);
  const label = step?.label ?? '';
  const heading = labelFor(format, field);
  return label && label.toLowerCase() !== heading.toLowerCase() ? label : '';
}

function labelFor(format: RecordFormat, field: RecordField): string {
  if (field === 'situation') return format.labels.situation;
  if (field === 'thought') return format.labels.thought;
  if (field === 'response') return format.labels.response;
  return '';
}
